import React, { useState, useEffect, useRef } from 'react';
import { 
  Building, Search, ShieldCheck, Briefcase, Wheat, Droplet, 
  Cookie, CupSoda, Container, Beef, MapPin, Award, Truck, Target, Croissant
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { mockData } from '../../mock';
import PictureImg from '../PictureImg';
import CatalogModal from './CatalogModal';

const iconMap = {
  building: Building,
  search: Search,
  'shield-check': ShieldCheck,
  briefcase: Briefcase,
  wheat: Wheat,
  droplet: Droplet,
  cheese: Cookie,
  croissant: Croissant,
  'cup-soda': CupSoda,
  container: Container,
  beef: Beef,
  'map-pin': MapPin,
  award: Award,
  truck: Truck,
  target: Target
};

const ProductsSection = () => {
  const [productsAnimated, setProductsAnimated] = useState(false);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState('');
  const productsRef = useRef(null);

  const openCatalogModal = (categoryName) => {
    setSelectedProductCategory(categoryName);
    setIsCatalogModalOpen(true);
  };

  useEffect(() => {
    const productsObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !productsAnimated) {
          setProductsAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (productsRef.current) {
      productsObserver.observe(productsRef.current);
    }

    return () => {
      if (productsRef.current) {
        productsObserver.unobserve(productsRef.current);
      }
    };
  }, [productsAnimated]);

  return (
    <>
      <section 
        id="products" 
        className="py-20 px-6 bg-white"
        ref={productsRef}
        data-testid="products-section"
      >
        <div className={`container mx-auto max-w-6xl ${productsAnimated ? 'products-animate-active' : 'products-animate-ready'}`}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-blue-900 font-semibold tracking-wide uppercase text-sm products-label">
              Product Categories
            </span>
            <h2 className="md:text-5xl !font-bold !text-4xl mt-3 mb-6 text-stone-900">
              <span className="products-headline">
                Authentic <span className="text-blue-900">Italian products</span>
              </span>
            </h2>
            <p className="text-lg text-stone-600 products-subtext">
              Access Italy&apos;s finest food products across diverse categories, all sourced with expertise and care.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.productCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Card
                  key={category.id}
                  className="border-stone-200 hover:border-blue-900 transition-all duration-300 hover:shadow-lg group cursor-pointer overflow-hidden"
                  data-testid={`product-card-${category.id}`}
                >
                  <div className="relative h-48 overflow-hidden bg-stone-100">
                    <PictureImg
                      base={category.base}
                      alt={category.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      width={category.width}
                      height={category.height}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
                        <IconComponent className="text-white" size={20} />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-stone-900 mb-2">
                      {category.href ? (
                        <Link
                          to={category.href}
                          className="hover:text-blue-900 transition-colors"
                          data-testid={`product-card-title-link-${category.id}`}
                        >
                          {category.name}
                        </Link>
                      ) : (
                        category.name
                      )}
                    </h3>
                    <Button 
                      className="bg-blue-900 hover:bg-blue-950 text-white transition-all duration-300 shadow-md hover:shadow-lg"
                      onClick={() => openCatalogModal(category.name)}
                      data-testid={`catalog-btn-${category.id}`}
                    >
                      Access the Catalog
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <CatalogModal 
        isOpen={isCatalogModalOpen} 
        onClose={() => setIsCatalogModalOpen(false)}
        productCategory={selectedProductCategory}
      />
    </>
  );
};

export default ProductsSection;
